import React, { useEffect, useState, useRef } from "react";
import Button from "@mui/material/Button";
import { motion } from "framer-motion";
import "./TechPortfolioLandingPage.css";
import EducationTopBar from "../topbar/EducationTopBar";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import RecognizedBySection from "../Certificate/RecognizedBySection";
import MarqueeScroll from "../reusables/MarqueeScroll";
import { workshopReviews } from "../../../utils/constants";
import WorkshopFooterPage from "../Workshops/WorkshopFooter";
import { useDispatch, useSelector } from "react-redux";
import { redirectToPageAction } from "../../../actions/userActions";
import CryptoJS from "crypto-js";

// Custom Intersection Observer Hook
const useIntersectionObserver = (options = {}) => {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [hasIntersected, setHasIntersected] = useState(false);
  const targetRef = useRef(null);

  useEffect(() => {
    const target = targetRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsIntersecting(entry.isIntersecting);
        if (entry.isIntersecting && !hasIntersected) {
          setHasIntersected(true);
        }
      },
      {
        threshold: options.threshold || 0.1,
        rootMargin: options.rootMargin || "0px",
      },
    );

    observer.observe(target);

    return () => {
      if (target) {
        observer.unobserve(target);
      }
    };
  }, [hasIntersected, options.threshold, options.rootMargin]);

  return [targetRef, hasIntersected];
};

const ReusableButton = ({ text, onClick }) => {
  return (
    <motion.div
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      transition={{ type: "spring", stiffness: 400, damping: 17 }}
    >
      <Button
        variant="contained"
        className="techPortfolioLandingPage_button shimmer"
        onClick={onClick}
      >
        {text}
      </Button>
    </motion.div>
  );
};

const IconSquare = ({ icon, text, index }) => {
  const [ref, isVisible] = useIntersectionObserver({
    threshold: 0.2,
    rootMargin: "-50px",
  });

  return (
    <motion.div
      ref={ref}
      className="techPortfolioLandingPage_iconSquare"
      initial={{ opacity: 0, y: 50 }}
      animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={{ scale: 1.05, backgroundColor: "#333" }}
    >
      <div className="techPortfolioLandingPage_icon">{icon}</div>
      <p>{text}</p>
    </motion.div>
  );
};

const GradientCard = ({ gradient, imageSrc, text, index }) => {
  const [ref, isVisible] = useIntersectionObserver({
    threshold: 0.2,
    rootMargin: "-50px",
  });

  return (
    <motion.div
      ref={ref}
      style={{ marginBottom: "20px" }}
      initial={{ opacity: 0, scale: 0.8, y: 50 }}
      animate={
        isVisible
          ? { opacity: 1, scale: 1, y: 0 }
          : { opacity: 0, scale: 0.8, y: 50 }
      }
      transition={{ duration: 0.6, delay: index * 0.15 }}
      whileHover={{ scale: 1.05, rotate: 2 }}
    >
      <div
        className="techPortfolioLandingPage_gradientOuter"
        style={{ background: gradient }}
      >
        <div className="techPortfolioLandingPage_gradientCard">
          <img src={imageSrc} alt="card" />
        </div>
      </div>
      <div>
        <h2>{text}</h2>
      </div>
    </motion.div>
  );
};

const StepItem = ({ icon, text, index }) => {
  const [ref, isVisible] = useIntersectionObserver({
    threshold: 0.3,
    rootMargin: "-50px",
  });

  return (
    <motion.div
      ref={ref}
      className="techPortfolioLandingPage_stepItem"
      initial={{ opacity: 0, x: -50 }}
      animate={isVisible ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
      transition={{ duration: 0.5, delay: index * 0.2 }}
      whileHover={{ scale: 1.03, backgroundColor: "#333" }}
    >
      <div className="techPortfolioLandingPage_stepIcon">{icon}</div>
      <p>{text}</p>
    </motion.div>
  );
};

const AnimatedSection = ({ children, className, delay = 0 }) => {
  const [ref, isVisible] = useIntersectionObserver({
    threshold: 0.1,
    rootMargin: "-80px",
  });

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 30 }}
      animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.6, delay }}
    >
      {children}
    </motion.div>
  );
};

const AnimatedDivider = () => {
  const [ref, isVisible] = useIntersectionObserver({
    threshold: 0.5,
    rootMargin: "0px",
  });

  return (
    <motion.div
      ref={ref}
      className="techPortfolioLandingPage_divider"
      initial={{ scaleX: 0 }}
      animate={isVisible ? { scaleX: 1 } : { scaleX: 0 }}
      transition={{ duration: 0.8 }}
    />
  );
};

export const TechPortfolioLandingPage = () => {
  const history = useHistory();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.userLogin);

  return (
    <div>
      <EducationTopBar
        logo={
          <div
            onClick={() => history.push("/")}
            style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
          >
            <img
              className={isMobile ? "mobile-logo" : "desktop-logo"}
              src="https://res.cloudinary.com/cqn/image/upload/v1740519381/white_acetechies_logo_vsdhp5.png"
              alt=""
            />
          </div>
        }
      />
      <div className="techPortfolioLandingPage_container">
        {/* First Section */}
        <section className="techPortfolioLandingPage_firstSection">
          <motion.h1
            className="techPortfolioLandingPage_title"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Your Skills Deserve a Stunning Portfolio
          </motion.h1>
          <motion.h3
            className="techPortfolioLandingPage_subtitle"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            A stunning, recruiter-friendly portfolio built for you. Just edit
            your details and share your public link.
          </motion.h3>
          <motion.div
            className="techPortfolioLandingPage_glassContainer"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            Your Portfolio. Ready to Publish 🚀
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
          >
            <ReusableButton
              text="View My Portfolio"
              onClick={() => {
                history.push(
                  `/techprofile/edit/${CryptoJS.lib.WordArray.random(
                    16,
                  ).toString()}`,
                );
                dispatch(
                  redirectToPageAction("EDIT_TECH_PROFILE_PAGE", {
                    url: `/techprofile/edit/${userInfo?._id}`,
                  }),
                );
              }}
            />
          </motion.div>
          <motion.video
            className="techPortfolioLandingPage_video"
            autoPlay
            muted
            loop
            playsInline
            src="https://res.cloudinary.com/cqn/video/upload/v1760267401/landing_page_video_fl3b0b.mp4"
            type="video/mp4"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1 }}
          />
        </section>

        {/* Second Section */}
        <section className="techPortfolioLandingPage_secondSection">
          <AnimatedSection>
            <h2 className="techPortfolioLandingPage_title">
              Resumes Don&apos;t Tell Your Full Story
            </h2>
          </AnimatedSection>
          <AnimatedSection delay={0.2}>
            <h4 className="techPortfolioLandingPage_subtitle">
              Recruiters want to see your projects, skills, and achievements
              &mdash; not just read about them.
            </h4>
          </AnimatedSection>
          <div className="techPortfolioLandingPage_iconRow">
            <IconSquare
              icon={<span>🥱</span>}
              text="Boring PDFs get lost"
              index={0}
            />
            <IconSquare
              icon={<span>🤔</span>}
              text="Details are Scattered everywhere"
              index={1}
            />
            <IconSquare
              icon={<span>⏳</span>}
              text="Building a portfolio takes time."
              index={2}
            />
          </div>
          <AnimatedDivider />
          <AnimatedSection>
            <h3 className="techPortfolioLandingPage_title">
              That&apos;s why we built AcePortfolio - your complete developer
              profile, ready to publish.
            </h3>
          </AnimatedSection>
          <AnimatedSection delay={0.2}>
            <ReusableButton
              text="Create My Portfolio"
              onClick={() => {
                history.push(`/techprofile/edit/${userInfo?._id}`);
                dispatch(
                  redirectToPageAction("EDIT_TECH_PROFILE_PAGE", {
                    url: `/techprofile/edit/${userInfo?._id}`,
                  }),
                );
              }}
            />
          </AnimatedSection>
        </section>

        {/* Third Section */}
        <section className="techPortfolioLandingPage_thirdSection">
          <div>
            <GradientCard
              gradient="linear-gradient(135deg, #FDE68A, #FBBF24)"
              imageSrc="https://res.cloudinary.com/cqn/image/upload/v1760265423/Screenshot_2025-10-12_160309_uvafx5.png"
              text="Portfolio home page"
              index={0}
            />
            <GradientCard
              gradient="linear-gradient(135deg, #A78BFA, #7C3AED)"
              imageSrc="https://res.cloudinary.com/cqn/image/upload/v1760265426/Screenshot_2025-10-12_160404_l5wtaf.png"
              text="Projects section"
              index={1}
            />
            <GradientCard
              gradient="linear-gradient(135deg, #7DD3FC, #0EA5E9)"
              imageSrc="https://res.cloudinary.com/cqn/image/upload/v1760265422/Screenshot_2025-10-12_160630_pvywxt.png"
              text="Certificates or GitHub stats"
              index={2}
            />
          </div>

          <AnimatedSection delay={0.4}>
            <ReusableButton
              text="Explore More"
              onClick={() => {
                history.push(`/techprofile/edit/${userInfo?._id}`);
                dispatch(
                  redirectToPageAction("EDIT_TECH_PROFILE_PAGE", {
                    url: `/techprofile/edit/${userInfo?._id}`,
                  }),
                );
              }}
            />
          </AnimatedSection>
        </section>

        <AnimatedSection>
          <RecognizedBySection
            title={
              "Our Portfolio Certificate is recognised by 1200+ Top Companies"
            }
          />
        </AnimatedSection>

        {/* Fourth Section */}
        <section className="techPortfolioLandingPage_fourthSection">
          <AnimatedSection>
            <h2 className="techPortfolioLandingPage_title">How It Works</h2>
          </AnimatedSection>
          <AnimatedSection delay={0.2}>
            <h4 className="techPortfolioLandingPage_subtitle">
              3 Steps to Your Live Portfolio
            </h4>
          </AnimatedSection>
          <div className="techPortfolioLandingPage_stepsContainer">
            <StepItem
              icon={<span>1️⃣</span>}
              text="Sign Up and Add Details"
              index={0}
            />
            <StepItem
              icon={<span>2️⃣</span>}
              text="Customize Your Portfolio"
              index={1}
            />
            <StepItem
              icon={<span>3️⃣</span>}
              text="Publish and Share"
              index={2}
            />
          </div>
          <AnimatedSection delay={0.6}>
            <ReusableButton
              text="Get Started"
              onClick={() => {
                history.push(`/techprofile/edit/${userInfo?._id}`);
                dispatch(
                  redirectToPageAction("EDIT_TECH_PROFILE_PAGE", {
                    url: `/techprofile/edit/${userInfo?._id}`,
                  }),
                );
              }}
            />
          </AnimatedSection>
        </section>

        {/* Fifth Section */}
        <section className="techPortfolioLandingPage_fifthSection">
          <AnimatedSection>
            <h2 className="techPortfolioLandingPage_title">
              Everything You Need to Impress Recruiters
            </h2>
          </AnimatedSection>
          <div className="techPortfolioLandingPage_featuresGrid">
            <GradientCard
              gradient="linear-gradient(135deg, #FDE68A, #FBBF24)"
              imageSrc="https://res.cloudinary.com/cqn/image/upload/v1760265591/Screenshot_2025-10-12_160742_ppmefa.png"
              text="Update details anytime."
              index={0}
            />
            <GradientCard
              gradient="linear-gradient(135deg, #A78BFA, #7C3AED)"
              imageSrc="https://res.cloudinary.com/cqn/image/upload/v1760265591/Screenshot_2025-10-12_160832_puv2ga.png"
              text="Highlight achievements beautifully."
              index={1}
            />
            <GradientCard
              gradient="linear-gradient(135deg, #7DD3FC, #0EA5E9)"
              imageSrc="https://res.cloudinary.com/cqn/image/upload/v1760265591/Screenshot_2025-10-12_160941_rtouov.png"
              text="Your unique link, instantly live."
              index={2}
            />
          </div>
          <AnimatedSection delay={0.4}>
            <ReusableButton
              text="Make my Portfolio"
              onClick={() => {
                history.push(`/techprofile/edit/${userInfo?._id}`);
                dispatch(
                  redirectToPageAction("EDIT_TECH_PROFILE_PAGE", {
                    url: `/techprofile/edit/${userInfo?._id}`,
                  }),
                );
              }}
            />
          </AnimatedSection>
        </section>
        <AnimatedSection>
          <h2 className="techPortfolioLandingPage_title">Reviews</h2>
          <MarqueeScroll content={workshopReviews} isReview={true} />
        </AnimatedSection>
      </div>
      <WorkshopFooterPage />
    </div>
  );
};
